import { useEffect, useMemo, useRef, useState } from 'react'

import { Layer, Stage, Transformer } from 'react-konva'
import useImage from 'use-image'
import styled from 'styled-components'

import { useAppThemeColors } from '../../../shared/hooks'

import { AnnotationMarker } from './components/AnnotationMarker'
import { AnnotationShape } from './components/AnnotationShape'
import { AnnotationOverlay } from './components/AnnotationOverlay'
import { NewAnnotationMarker } from './components/NewAnnotationMarker'
import { useAnnotationCreation } from './hooks/useAnnotationCreation'
import { useAnnotationMarkers } from './hooks/useAnnotationMarkers'
import {
  CREATING_NEW_ANNOTATION_MODE,
  shouldShowAnnotationHighlight,
  getActiveRectangle,
  transformAnnotationsToShapes, ANNOTATION_TYPE,
  ANNOTATION_OUTSIDE_CLICK_PREVENT_CLASS
} from './utils/annotationUtils'

interface CanvasAnnotationsProps {
  className?: string
  parentRef: React.RefObject<HTMLDivElement>,
  assetRef: React.RefObject<any>,
  assetBoundingBox: { width: number, height: number }
  isVisible: boolean
  currentZoom: number
  position?: { x: number, y: number }
  isDragging?: boolean
  previewUrl: string
  annotations: any[]
  creatingNewAnnotationMode: CREATING_NEW_ANNOTATION_MODE
  onAnnotationSelect?: (annotation: any) => void
  onAnnotationSubmit?: (annotation: any) => void
  onAnnotationClick?: (annotation: any) => void
  onFinishImgLoading?: () => void
  onErrorImgLoading?: () => void
  onActiveAnnotation?: (annotation: any) => void
  newAnnotation: any
  selectedAnnotation: any
  onCreateNewAnnotation: (annotation: any) => void
  onStartCreatingNewAnnotation: () => void
  onFinishCreatingNewAnnotation: () => void
  onClearNewAnnotation: () => void
  showMarkers?: boolean
  stageMargin?: number
  onCanvasClickOutside?: () => void
}

interface SContainerProps {
  $creatingNewAnnotationMode: CREATING_NEW_ANNOTATION_MODE | null;
}

export const CanvasAnnotations = ({
  className,
  assetBoundingBox,
  isVisible,
  currentZoom,
  previewUrl,
  annotations,
  creatingNewAnnotationMode,
  onAnnotationSelect,
  onFinishImgLoading,
  onErrorImgLoading,
  newAnnotation,
  selectedAnnotation,
  onCreateNewAnnotation,
  onStartCreatingNewAnnotation = () => undefined,
  onFinishCreatingNewAnnotation = () => undefined,
  onClearNewAnnotation,
  showMarkers = true,
  stageMargin,
  onCanvasClickOutside
}: CanvasAnnotationsProps) => {

  const stageRef = useRef(null)
  const transformerRef = useRef(null)
  const [fillColor] = useState("rgb(255,255,255)")
  const [stageBounds, setStageBounds] = useState({ width: 0, height: 0 })
  const [showOverlay, setShowOverlay] = useState(false)
  const [showNewMarker, setShowNewMarker] = useState(false)

  const [, status] = useImage(previewUrl)
  const currentScale = currentZoom / 100
  const selectedAnnotationId = selectedAnnotation?.data?.id

  // Используем кастомные хуки
  const {
    isClickingMarker,
    calculateMarkerPosition,
    handleMarkerClick,
    handleMarkerMouseEnter,
    handleMarkerMouseLeave
  } = useAnnotationMarkers({
    currentZoom,
    assetBoundingBox,
    onAnnotationSelect,
    stageRef
  })

  const {
    annotationInProgress,
    setAnnotationInProgress,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onMouseLeave
  } = useAnnotationCreation({
    creatingNewAnnotationMode,
    onStartCreatingNewAnnotation,
    onFinishCreatingNewAnnotation,
    onClearNewAnnotation,
    onCreateNewAnnotation,
    onAnnotationSelect,
    selectedAnnotation,
    isClickingMarker
  })

  // Мемоизированные вычисления
  const annotationsShapes = useMemo(() => {
    return transformAnnotationsToShapes(annotations, selectedAnnotationId, fillColor)
  }, [selectedAnnotation, fillColor, annotations, stageBounds])

  const rectAnnotations = useMemo(() => {
    return annotationsShapes.filter((annotation) => annotation.type === ANNOTATION_TYPE.RECTANGLE)
  }, [annotationsShapes])

  const pointAnnotations = useMemo(() => {
    return annotationsShapes.filter((annotation) => annotation.type === ANNOTATION_TYPE.POINT)
  }, [annotationsShapes])

  const shouldShowHighlight = useMemo(() => {
    return shouldShowAnnotationHighlight(selectedAnnotation, annotationInProgress, showOverlay)
  }, [selectedAnnotation, annotationInProgress, showOverlay])

  const activeRectangle = useMemo(() => {
    return getActiveRectangle(selectedAnnotation, annotationInProgress)
  }, [selectedAnnotation, annotationInProgress])

  // Эффекты
  useEffect(() => {
    if(!isVisible) {
      setAnnotationInProgress(null)
    }
  }, [isVisible])

  useEffect(() => {
    const { width, height } = assetBoundingBox
    if(width && height && isVisible) {
      setStageBounds({
        width: width + (stageMargin || 0),
        height: height + (stageMargin || 0)
      })
    }
  }, [assetBoundingBox.width, assetBoundingBox.height, isVisible, stageMargin])

  useEffect(() => {
    setAnnotationInProgress(newAnnotation)
  }, [newAnnotation])

  useEffect(() => {
    if (status === 'loaded' && onFinishImgLoading) {
      onFinishImgLoading()
    } else if (status === 'failed' && onErrorImgLoading) {
      onErrorImgLoading()
    }
  }, [status, onFinishImgLoading, onErrorImgLoading])

  // Обработчик клика за пределами канваса
  useEffect(() => {
    if (!onCanvasClickOutside || !isVisible) return

    const handleClickOutside = (event: MouseEvent) => {
      if (!stageRef.current) return
      const CANVAS_CONTENT_CLASS = 'konvajs-content'
      const stage = stageRef.current
      const stageContainer = stage.container()
      const clickedElement = event.target as Element
      const canvasContent = stageContainer.querySelector(`.${CANVAS_CONTENT_CLASS}`)
      // Проверяем, что клик был не внутри канваса
      if (!canvasContent.contains(clickedElement) && !clickedElement.closest(`.${ANNOTATION_OUTSIDE_CLICK_PREVENT_CLASS}`)) {
        onCanvasClickOutside()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onCanvasClickOutside, isVisible])

  const isDraggable = creatingNewAnnotationMode === null

  const handlePointerDown = (e: any) => {
    setShowOverlay(false)
    setShowNewMarker(false)
    onPointerDown(e, stageRef)
  }

  const handlePointerMove = () => {
    onPointerMove(stageRef)
  }

  const handlePointerUp = () => {
    onPointerUp({ onShowOverlay: () => {
      setShowOverlay(true)
    } })
    setShowNewMarker(true)
  }

  const handleMouseLeave = () => {
    onMouseLeave()
  }

  const handleShapeClick = (e: any) => {
    if (creatingNewAnnotationMode !== null) return
    const target = e.currentTarget
    transformerRef.current.nodes([target])
  }

  const getContainerStyles = () => {
    return {
      display: !isVisible ? 'none' : 'flex',
    }
  }

  const isNewAnnotation = annotationInProgress && Object.keys(annotationInProgress).length > 0
  const themeColors = useAppThemeColors()
  const strokeColor = themeColors.primary

  return (
    <SContainer
      className={className}
      style={getContainerStyles()}
      $creatingNewAnnotationMode={creatingNewAnnotationMode}
    >
      <SStage
        ref={stageRef}
        width={stageBounds.width}
        height={stageBounds.height}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onMouseLeave={handleMouseLeave}
      >
        {/* Слой с затемнением */}
        {shouldShowHighlight && (
          <AnnotationOverlay
            stageBounds={stageBounds}
            activeRectangle={activeRectangle}
          />
        )}

        <Layer>
          {/* Существующие аннотации */}
          {rectAnnotations.map((rectangle) => (
            <AnnotationShape
              strokeColor={strokeColor}
              key={rectangle.id}
              annotation={rectangle}
              isDraggable={isDraggable}
              onClick={handleShapeClick}
            />
          ))}

          {pointAnnotations.map((point) => (
            <AnnotationShape
              strokeColor={strokeColor}
              key={point.id}
              annotation={point}
              isDraggable={isDraggable}
              onClick={handleShapeClick}
            />
          ))}

          {/* Новая создаваемая аннотация */}
          {isNewAnnotation && (
            <AnnotationShape
              strokeColor={strokeColor}
              annotation={annotationInProgress}
              isDraggable={isDraggable}
              onClick={handleShapeClick}
            />
          )}

          {/*<Transformer ref={transformerRef} />*/}
        </Layer>

        {/* Слой с маркерами */}
        {showMarkers && (
          <Layer>
            {annotations.map((annotation) => {
              const position = calculateMarkerPosition(annotation)
              if (!position) return null

              const isActive = annotation.data.id === selectedAnnotationId

              return (
                <AnnotationMarker
                  key={annotation.data.id}
                  annotation={annotation}
                  isActive={isActive}
                  position={{ x: position.x, y: position.y }}
                  squareSize={position.size}
                  markerDirection={position.direction as 'bottom-left' | 'bottom-right' | 'top-left'}
                  currentScale={currentScale}
                  onClick={handleMarkerClick}
                  onMouseEnter={handleMarkerMouseEnter}
                  onMouseLeave={handleMarkerMouseLeave}
                />
              )
            })}
          </Layer>
        )}

        {/* Слой с маркером для создаваемой аннотации */}
        {showMarkers && showNewMarker && isNewAnnotation && (
          <Layer>
            <NewAnnotationMarker
              annotationInProgress={annotationInProgress}
              currentZoom={currentZoom}
              currentScale={currentScale}
            />
          </Layer>
        )}
      </SStage>
    </SContainer>
  )
}

const SContainer = styled.div<SContainerProps>`
  width: 100%;
  height: 100%;
  :hover {
    cursor: ${({ $creatingNewAnnotationMode }) => $creatingNewAnnotationMode !== null ? 'url(/assets/cursors/msgCursor.svg) 0 15, auto' : 'default'};
  }
`

const SStage = styled(Stage)`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`
